import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Filter, RefreshCw, Download } from "lucide-react";
import { TIMETABLE } from "@/data/timetable";
import { AdminBookingRow } from "./AdminBookingRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick filter presets — each builds a complete filter payload that gets
// applied in one click. Editable from this file alone.
function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function isoToday() {
  return new Date().toISOString().slice(0, 10);
}
function isoMonthStart() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function isoMonthsAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}

const EMPTY_FILTERS = {
  location: "", age_group: "", class_type: "", category: "",
  date_from: "", date_to: "", payment_status: "",
  attendance_status: "", attendance_missing: false, student_status: "",
};

const QUICK_FILTERS = [
  { id: "today",            label: "Today",              patch: { date_from: isoToday, date_to: isoToday } },
  { id: "last7",            label: "Last 7 days",        patch: { date_from: () => isoDaysAgo(7), date_to: isoToday } },
  { id: "this_month",       label: "This month",         patch: { date_from: isoMonthStart, date_to: isoToday } },
  { id: "kids",             label: "Kids only",          patch: { category: () => "kids" } },
  { id: "adults",           label: "Adults only",        patch: { category: () => "adult" } },
  { id: "workshops",        label: "Workshops",          patch: { category: () => "workshop" } },
  { id: "online",           label: "Online",             patch: { category: () => "online" } },
  { id: "unpaid",           label: "Unpaid",             patch: { payment_status: () => "unpaid" } },
  { id: "paid",             label: "Paid",               patch: { payment_status: () => "paid" } },
  { id: "medway",           label: "Medway",             patch: { location: () => "Medway" } },
  { id: "london",           label: "London",             patch: { location: () => "Elephant & Castle" } },
  { id: "attended_term",    label: "Attended this term", patch: { attendance_status: () => "attended", date_from: () => isoMonthsAgo(3), date_to: isoToday } },
  { id: "missing_attend",   label: "Missing attendance", patch: { attendance_missing: () => true, payment_status: () => "paid" } },
  { id: "new_students",     label: "New students",       patch: { student_status: () => "new" } },
  { id: "returning",        label: "Returning students", patch: { student_status: () => "returning" } },
];

// Pulls distinct values from timetable for fast filtering
const ALL_LOCATIONS = Array.from(new Set(TIMETABLE.map((s) => s.location))).sort();

export const AdminBookings = ({ pw }) => {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ locations: [], age_groups: [] });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    location: "", age_group: "", class_type: "", category: "",
    date_from: "", date_to: "", payment_status: "",
    attendance_status: "", attendance_missing: false, student_status: "",
  });
  const [activeQuick, setActiveQuick] = useState(null);
  const [search, setSearch] = useState("");

  // Editable in-row state per row
  const [editing, setEditing] = useState({}); // { [session_id]: { attendance_notes } }
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (typeof v === "boolean") { if (v) params[k] = "true"; return; }
        if (v) params[k] = v;
      });
      const r = await axios.get(`${API}/admin/bookings`, {
        headers: { "x-admin-password": pw },
        params,
      });
      setItems(r.data.items || []);
      setFacets(r.data.facets || { locations: [], age_groups: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [filters, pw]);

  useEffect(() => { load(); }, [load]);

  // Local search filter — applied AFTER server filtering for speed
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const b = it.booking || {};
      return [
        b.student_name, b.guardian_name, b.email, b.phone,
        b.class_type, b.location, it.sku, it.session_id,
      ].some((v) => (v || "").toString().toLowerCase().includes(q));
    });
  }, [items, search]);

  const locations = useMemo(
    () => Array.from(new Set([...ALL_LOCATIONS, ...(facets.locations || [])])).filter(Boolean).sort(),
    [facets.locations]
  );

  const saveRow = async (it) => {
    const ed = editing[it.session_id] || {};
    try {
      setSavingId(it.session_id);
      const patch = {};
      if ("attendance_notes" in ed) patch.attendance_notes = ed.attendance_notes;
      if ("attendance_status" in ed) patch.attendance_status = ed.attendance_status;
      const r = await axios.patch(`${API}/admin/bookings/${encodeURIComponent(it.session_id)}`,
        patch,
        { headers: { "x-admin-password": pw } });
      setItems((prev) => prev.map((p) => p.session_id === it.session_id ? r.data : p));
      setEditing((prev) => ({ ...prev, [it.session_id]: undefined }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const downloadCsv = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (typeof v === "boolean") { if (v) params[k] = "true"; return; }
        if (v) params[k] = v;
      });
      const r = await axios.get(`${API}/admin/bookings/export.csv`, {
        headers: { "x-admin-password": pw },
        params,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `usdd-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const applyQuick = (preset) => {
    if (activeQuick === preset.id) {
      // Toggle off
      setFilters({ ...EMPTY_FILTERS });
      setActiveQuick(null);
      return;
    }
    const next = { ...EMPTY_FILTERS };
    Object.entries(preset.patch).forEach(([k, fn]) => {
      next[k] = typeof fn === "function" ? fn() : fn;
    });
    setFilters(next);
    setActiveQuick(preset.id);
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setActiveQuick(null);
    setSearch("");
  };

  return (
    <div data-testid="admin-bookings-tab">
      {/* Quick filters row */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 sm:p-5 mb-3" data-testid="admin-quick-filters">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-yellow animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow font-bold">Quick filters</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {(activeQuick || Object.entries(filters).some(([, v]) => v) || search) && (
              <button
                onClick={clearFilters}
                data-testid="admin-bookings-clear-filters"
                className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 hover:text-brand-yellow"
              >
                Clear all ×
              </button>
            )}
            <button
              onClick={downloadCsv}
              data-testid="admin-bookings-export-csv"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-brand-yellow text-black text-[10px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover disabled:opacity-50 transition-colors"
            >
              <Download size={11} /> Export filtered CSV
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_FILTERS.map((q) => {
            const active = activeQuick === q.id;
            return (
              <button
                key={q.id}
                onClick={() => applyQuick(q)}
                data-testid={`admin-quick-${q.id}`}
                className={`px-3.5 py-1.5 rounded-full text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] transition-all ${
                  active
                    ? "bg-brand-yellow text-black glow-yellow"
                    : "bg-white/[0.04] border border-white/15 text-zinc-300 hover:border-brand-yellow/50 hover:text-brand-yellow"
                }`}
              >
                {q.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 sm:p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-brand-yellow" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow font-bold">Filters</span>
          <button
            onClick={load}
            data-testid="admin-bookings-refresh"
            className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-zinc-300 hover:text-brand-yellow"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <select
            data-testid="admin-bookings-filter-location"
            value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            className="ad-input"
          >
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select
            data-testid="admin-bookings-filter-age"
            value={filters.age_group} onChange={(e) => setFilters({ ...filters, age_group: e.target.value })}
            className="ad-input"
          >
            <option value="">All ages</option>
            {(facets.age_groups || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input
            type="text" placeholder="Class type contains…"
            data-testid="admin-bookings-filter-class"
            value={filters.class_type} onChange={(e) => setFilters({ ...filters, class_type: e.target.value })}
            className="ad-input"
          />
          <select
            data-testid="admin-bookings-filter-status"
            value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })}
            className="ad-input"
          >
            <option value="">Any status</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid (any non-paid)</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="expired">Expired</option>
          </select>
          <input
            type="date" data-testid="admin-bookings-filter-from"
            value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="ad-input"
          />
          <input
            type="date" data-testid="admin-bookings-filter-to"
            value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="ad-input"
          />
        </div>

        <div className="mt-3 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="admin-bookings-search"
            type="text" placeholder="Search name / email / phone / class / order ID…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="ad-input pl-9"
          />
        </div>

        <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          Showing {visible.length} of {items.length} bookings
        </div>
      </div>

      {/* List */}
      {loading && <div className="text-sm text-zinc-400">Loading…</div>}
      {!loading && visible.length === 0 && (
        <div data-testid="admin-bookings-empty" className="text-center py-10 text-sm text-zinc-500 border border-dashed border-white/10 rounded-2xl">
          No bookings match these filters yet.
        </div>
      )}

      <div className="space-y-3">
        {visible.map((it) => (
          <AdminBookingRow
            key={it.session_id}
            it={it}
            editing={editing}
            setEditing={setEditing}
            savingId={savingId}
            onSave={saveRow}
          />
        ))}
      </div>
    </div>
  );
};
